import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  displayName,
  formatBytes,
  stationHealth,
  type Customer,
  type StationDetail as Detail,
  type UserProfile,
} from '@visionqc/shared';
import { api } from '../api/client';
import { CustomerPill } from '../components/CustomerPill';
import { fmtTime } from '../lib/format';

/**
 * One station, in full.
 *
 * This is what someone opens when a line has gone quiet, so the two questions
 * it answers first are "who do I call" and "when did it last do anything".
 * Everything else is below that.
 */

const STATUS: Record<string, { label: string; color: string; icon: string }> = {
  online: { label: 'Online', color: 'var(--good)', icon: '●' },
  late: { label: 'Late', color: 'var(--warn)', icon: '▲' },
  offline: { label: 'Offline', color: 'var(--bad)', icon: '■' },
};

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ minWidth: 110 }}>
      <div
        className="muted"
        style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {hint && (
        <div className="muted" style={{ fontSize: 11 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export function StationDetailView({ id, onBack }: { id: number; onBack: () => void }) {
  const [d, setD] = useState<Detail | null>(null);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setD(await api.station(id));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [id]);

  useEffect(() => {
    void load();
    void api.listUsers().then(setUsers).catch(() => setUsers([]));
    void api.listCustomers().then(setCustomers).catch(() => setCustomers([]));
  }, [load]);

  if (error) return <div className="panel" style={{ color: 'var(--bad)' }}>{error}</div>;
  if (!d) return <div className="spinner">Loading station…</div>;

  const health = d.active ? stationHealth(d.lastSeenAt) : 'offline';
  const h = STATUS[health]!;
  const s = d.stats;
  const peak = Math.max(1, ...d.series.map((p) => p.inspections));

  const save = async () => {
    try {
      await api.updateStation(d.id, form);
      setEditing(false);
      setForm({});
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const field = (key: string, label: string, current: string | null) => (
    <div style={{ flex: 1, minWidth: 150 }}>
      <label>{label}</label>
      <input
        value={(form[key] as string) ?? current ?? ''}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        style={{ width: '100%' }}
      />
    </div>
  );

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <button className="btn sm" onClick={onBack}>
            ← All stations
          </button>
          <h1 className="view-title" style={{ marginTop: 8 }}>
            {d.name}
            {d.lineName && <span className="muted" style={{ fontWeight: 400 }}> · {d.lineName}</span>}
          </h1>
        </div>
        <div className="row">
          <span style={{ color: h.color, fontSize: 15 }}>
            {h.icon} {h.label}
          </span>
          <button className="btn sm" onClick={() => setEditing((v) => !v)}>
            {editing ? '✕ Cancel' : '✎ Edit'}
          </button>
        </div>
      </div>

      {/* Who to call, first — that is what this page is opened for. */}
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        <div className="panel">
          <strong>Contact</strong>
          {editing ? (
            <div className="grid" style={{ gap: 12, marginTop: 12 }}>
              <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <label>Responsible user</label>
                  <select
                    value={String((form.ownerUserId as number) ?? d.ownerUserId ?? '')}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        ownerUserId: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                    style={{ width: '100%' }}
                  >
                    <option value="">— nobody linked —</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {displayName(u)}
                        {u.companyName ? ` · ${u.companyName}` : ''}
                      </option>
                    ))}
                  </select>
                  <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
                    Their name, email, phone and company fill in below automatically.
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <label>Customer (who pays)</label>
                  {/*
                    No "unassigned" option any more. A station is the billable
                    unit and stations.customer_id is NOT NULL, so choosing it
                    sent a null the API now rejects — an option that renders
                    fine, reads as legal, and fails with a validation error.
                    A door that no longer opens should not be on the wall.
                  */}
                  <select
                    value={String((form.customerId as number) ?? d.customerId ?? '')}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, customerId: Number(e.target.value) }))
                    }
                    style={{ width: '100%' }}
                  >
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                {field('name', 'Station name', d.name)}
                {field('lineName', 'Line', d.lineName)}
              </div>
              <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                {field('contactName', 'Contact person', d.contactName)}
                {field('contactEmail', 'Email', d.contactEmail)}
                {field('contactPhone', 'Phone', d.contactPhone)}
              </div>
              <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                {field('companyName', 'Company', d.companyName)}
                {field('street', 'Street', d.street)}
                {field('houseNumber', 'No.', d.houseNumber)}
              </div>
              <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                {field('postalCode', 'Postcode', d.postalCode)}
                {field('city', 'City', d.city)}
                {field('country', 'Country', d.country)}
              </div>
              <div>
                <label>Notes</label>
                <textarea
                  rows={2}
                  value={(form.notes as string) ?? d.notes ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="row">
                <button className="btn primary" onClick={() => void save()}>
                  Save
                </button>
              </div>
            </div>
          ) : (
            <table style={{ marginTop: 8 }}>
              <tbody>
                <Row label="Person" value={d.contactName} />
                <Row label="Email" value={d.contactEmail} link={d.contactEmail ? `mailto:${d.contactEmail}` : null} />
                <Row label="Phone" value={d.contactPhone} link={d.contactPhone ? `tel:${d.contactPhone}` : null} />
                <Row label="Company" value={d.companyName} />
                <Row
                  label="Address"
                  value={
                    [
                      [d.street, d.houseNumber].filter(Boolean).join(' '),
                      [d.postalCode, d.city].filter(Boolean).join(' '),
                      d.country,
                    ]
                      .filter(Boolean)
                      .join(', ') || null
                  }
                />
                <Row
                  label="Customer"
                  value={<CustomerPill name={d.customer} hue={d.customerHue} />}
                />
                {d.owner && (
                  <Row
                    label="Linked account"
                    value={`${d.owner.name} (${d.owner.login})`}
                  />
                )}
                {d.notes && <Row label="Notes" value={d.notes} />}
              </tbody>
            </table>
          )}
        </div>

        <div className="panel">
          <strong>Machine</strong>
          <table style={{ marginTop: 8 }}>
            <tbody>
              {/* Observed, not configured — behind NAT this is the site's
                  public address rather than the machine's own. */}
              <Row label="Last seen from" value={d.lastIp} />
              <Row label="Last contact" value={d.lastSeenAt ? fmtTime(d.lastSeenAt) : 'never'} />
              <Row label="Last delivered rows" value={d.lastSyncAt ? fmtTime(d.lastSyncAt) : 'never'} />
              <Row label="App version" value={d.appVersion} />
              <Row label="Enrolled" value={fmtTime(d.createdAt)} />
              <Row label="Key rotated" value={d.keyRotatedAt ? fmtTime(d.keyRotatedAt) : 'never'} />
              <Row label="Station id" value={d.stationUid} />
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel" style={{ marginTop: 16 }}>
        <strong>Totals</strong>
        <div className="row" style={{ gap: 28, flexWrap: 'wrap', marginTop: 12 }}>
          <Stat label="Inspections" value={s.inspections.toLocaleString()} hint={`${s.last24h} in 24h`} />
          <Stat label="Good" value={s.good.toLocaleString()} />
          <Stat label="Bad" value={s.bad.toLocaleString()} hint={`${(s.rejectRate * 100).toFixed(1)}% reject`} />
          <Stat label="Unsure" value={s.unsure.toLocaleString()} hint="sent to a person" />
          <Stat label="Annotations" value={s.annotations.toLocaleString()} hint={`${s.annotators} people`} />
          <Stat label="Products" value={String(s.products)} />
          <Stat label="Datasets" value={String(s.datasets)} />
          <Stat label="Data received" value={formatBytes(s.bytesReceived)} hint="images uploaded" />
          <Stat label="Avg latency" value={`${s.avgLatencyMs} ms`} />
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <div className="panel">
          <strong>Last 14 days</strong>
          {d.series.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>Nothing in the last two weeks.</p>
          ) : (
            <div className="row" style={{ alignItems: 'flex-end', gap: 3, height: 90, marginTop: 12 }}>
              {d.series.map((p) => (
                <div
                  key={p.day}
                  title={`${p.day}: ${p.inspections} inspections, ${p.bad} bad`}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
                >
                  {/* Bad stacked on top of good, so the height is the day's
                      volume and the red portion is its reject share. */}
                  <div
                    style={{
                      height: `${(p.bad / peak) * 80}px`,
                      background: 'var(--bad)',
                      borderRadius: '3px 3px 0 0',
                    }}
                  />
                  <div
                    style={{
                      height: `${((p.inspections - p.bad) / peak) * 80}px`,
                      background: 'var(--good)',
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <strong>By product</strong>
          {d.byProduct.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>No inspections yet.</p>
          ) : (
            <table style={{ marginTop: 8 }}>
              <tbody>
                {d.byProduct.map((p) => (
                  <tr key={p.product}>
                    <td>{p.product}</td>
                    <td style={{ textAlign: 'right' }}>{p.inspections.toLocaleString()}</td>
                    <td style={{ textAlign: 'right', color: 'var(--bad)' }}>
                      {p.bad ? `${p.bad} bad` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {d.recent.length > 0 && (
        <div className="panel" style={{ marginTop: 16 }}>
          <strong>Most recent inspections</strong>
          <table style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>When</th>
                <th>Product</th>
                <th>Verdict</th>
                <th style={{ textAlign: 'right' }}>Score</th>
              </tr>
            </thead>
            <tbody>
              {d.recent.map((r) => (
                <tr key={r.id}>
                  <td className="muted">{fmtTime(r.time)}</td>
                  <td>{r.product}</td>
                  <td>
                    <span className={`badge ${r.verdict.toLowerCase()}`}>{r.verdict}</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>{r.score.toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  link,
}: {
  label: string;
  // Nodes as well as strings: the customer cell is a pill, not text.
  value: React.ReactNode;
  link?: string | null;
}) {
  return (
    <tr>
      <td className="muted" style={{ width: 150, borderBottom: 'none', padding: '5px 0' }}>
        {label}
      </td>
      <td style={{ borderBottom: 'none', padding: '5px 0' }}>
        {value ? link ? <a href={link}>{value}</a> : value : <span className="muted">—</span>}
      </td>
    </tr>
  );
}
